import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FeedbackContext, LocalizationContext } from '@cvt/contexts';

import { analysisClient } from '@modules/Analysis/client/analysisClient';
import { mapContextualAnalysisData } from '@modules/Analysis/helpers/helper';
import { cacheKeys } from '@modules/Stories/config';
import { cacheKeys as meetingsCacheKeys } from '@modules/Meetings/config';


export const useAnalysisCrud = () => {

  const { dictionary } = React.useContext(LocalizationContext);
  const { triggerFeedback, genericErrorFeedback } = React.useContext(FeedbackContext);
  const queryClient = useQueryClient();


  const createStructuralAnalysis = useMutation({
    mutationFn: (data: Partial<Stories.Edit>) => analysisClient.createStructuralAnalysis({ 'id': data.id }),
    onSuccess: (_response, data) => {
      const storyId = data.id;
      queryClient.invalidateQueries({
        queryKey: [cacheKeys.getStories],
      });
      queryClient.invalidateQueries({
        queryKey: [cacheKeys.getStory, storyId],
      });
      triggerFeedback({
        severity: 'success',
        message: dictionary.feedback.changesSaved,
      });
    },
    onError: () => {
      genericErrorFeedback();
    },
  });

  const createContextualAnalysis = useMutation({
    mutationFn: async (data: Partial<Stories.StoryThrough>) => {
      const res = await analysisClient.createContextualAnalysis({
        'id': data.id,
        'meetingAdaptation': data.meetingAdaptation,
        'momentOfReflection': data.momentOfReflection,
      });

      const responseData = (res.data as any) as Stories.ContextualFeedback;
      return mapContextualAnalysisData(responseData);
    },
    onSuccess: (_response, data) => {
      const meetingId = data.story?.id;
      queryClient.invalidateQueries({
        queryKey: [meetingsCacheKeys.getMeeting, meetingId],
      });
      triggerFeedback({
        severity: 'success',
        message: dictionary.feedback.changesSaved,
      });
    },
    onError: () => {
      genericErrorFeedback();
    },
  });

  return {
    createStructuralAnalysis,
    createContextualAnalysis,
  };

};
